import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { UserPlus, Trash2, Edit2 } from 'lucide-react';
import { UserDialog } from '../components/UserDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Users() {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, {
        withCredentials: true,
      });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;

    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, {
        withCredentials: true,
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error al eliminar usuario');
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrador',
      consultora: 'Consultora',
      doctor: 'Doctor',
      recepcion: 'Recepción',
      enfermero: 'Enfermero',
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-[#F3EBE1] rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1C18] tracking-tight">Usuarios</h1>
          <p className="text-[#6A736B] mt-1 text-sm">Gestión de usuarios del sistema</p>
        </div>
        <Button
          data-testid="add-user-button"
          onClick={() => {
            setSelectedUser(null);
            setDialogOpen(true);
          }}
          className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white rounded-lg transition-colors duration-200 w-full sm:w-auto"
        >
          <UserPlus className="mr-2" size={20} />
          Nuevo Usuario
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {users.map((user) => (
          <Card
            key={user.id}
            data-testid={`user-card-${user.email}`}
            className="bg-[#FFFFFF] rounded-xl border border-[#E8E5DD] shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:-translate-y-[2px] transition-all duration-200"
          >
            <CardHeader>
              <CardTitle className="text-lg text-[#1A1C18]">{user.name}</CardTitle>
              <p className="text-sm text-[#6A736B]">{user.email}</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {user.color && (
                    <div className="w-4 h-4 rounded-full border border-white shadow-sm" style={{ backgroundColor: user.color }} />
                  )}
                  <span className="px-2.5 py-0.5 bg-[#F3EBE1] text-[#2B4C3B] text-xs font-semibold rounded-full">
                    {getRoleLabel(user.role)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-user-${user.email}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setDialogOpen(true);
                    }}
                    className="text-[#2B4C3B] hover:bg-[#F3EBE1] hover:text-[#2B4C3B]"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    data-testid={`delete-user-${user.email}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(user.id)}
                    className="text-[#A65C5C] hover:bg-red-50 hover:text-[#A65C5C]"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {dialogOpen && (
        <UserDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedUser(null);
            fetchUsers();
          }}
          user={selectedUser}
        />
      )}
    </div>
  );
}